import { UUID } from "io-ts-types/UUID";

import { PersonalUserResponse } from "@every.org/common/src/codecs/entities";
import {
  AdminLevel,
  atLeastAsPrivilegedAs,
  FundraiserType,
  NonprofitAdminStatus,
} from "@every.org/common/src/entity/types";
import { FundraiserResponse } from "@every.org/common/src/routes/fundraiser";

function isSuperAdmin(loggedInUser: PersonalUserResponse): boolean {
  return atLeastAsPrivilegedAs({
    level: loggedInUser.adminLevelForDisplay,
    compareAgainst: AdminLevel.SUPER_ADMIN,
  });
}

export function isConfirmedNonprofitAdmin(
  nonprofitId: string | null | undefined,
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  if (!nonprofitId || !loggedInUser) {
    return false;
  }

  return (loggedInUser.adminFor ?? []).some(
    (item) =>
      item.status === NonprofitAdminStatus.CONFIRMED &&
      item.nonprofit.id === nonprofitId
  );
}

/**
 * Mirrors the fundraiser update-post API owner rule: the fundraiser's personal
 * creator, a super admin, or an admin of the nonprofit that created it.
 */
export function canAuthorFundraiserUpdates(
  fundraiser: FundraiserResponse["fundraiser"],
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  if (!loggedInUser) {
    return false;
  }

  return (
    isPersonalFundraiserCreator(fundraiser, loggedInUser) ||
    isSuperAdmin(loggedInUser) ||
    isConfirmedNonprofitAdmin(fundraiser.creatorNonprofitId, loggedInUser)
  );
}

/**
 * Beneficiary nonprofit admins may moderate a personal fundraiser's updates,
 * but may not author or edit them.
 */
export function canModeratePeerToPeerFundraiserUpdates(
  fundraiser: FundraiserResponse["fundraiser"],
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  return (
    isFundraiserAdmin(fundraiser, loggedInUser) &&
    !canAuthorFundraiserUpdates(fundraiser, loggedInUser)
  );
}

export function isPersonalFundraiserCreator(
  fundraiser: FundraiserResponse["fundraiser"],
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  return !!(
    loggedInUser &&
    !fundraiser.creatorNonprofitId &&
    loggedInUser.id === fundraiser.creatorUserId
  );
}

export function isFundraiserAdmin(
  fundraiser: FundraiserResponse["fundraiser"],
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  if (!loggedInUser) {
    return false;
  }

  if (isSuperAdmin(loggedInUser)) {
    return true;
  }

  const adminFor = loggedInUser.adminFor ?? [];
  const confirmedAdminIds = new Set(
    adminFor
      .filter((item) => item.status === NonprofitAdminStatus.CONFIRMED)
      .map((item) => item.nonprofit.id)
  );

  // Check if user is admin of the creator nonprofit
  if (
    fundraiser.creatorNonprofitId &&
    confirmedAdminIds.has(fundraiser.creatorNonprofitId)
  ) {
    return true;
  }

  // For single-nonprofit fundraisers, also allow nonprofit admins of the target nonprofit
  if (
    fundraiser.type !== FundraiserType.MULTIPLE_NONPROFITS &&
    confirmedAdminIds.has(fundraiser.nonprofitId)
  ) {
    return true;
  }

  return false;
}

export function canEditOrEndFundraiser(
  fundraiser: FundraiserResponse["fundraiser"],
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  return (
    isPersonalFundraiserCreator(fundraiser, loggedInUser) ||
    isFundraiserAdmin(fundraiser, loggedInUser)
  );
}

/*
 * Returns a list of nonprofit IDs where the user has CONFIRMED admin status.
 */
export function getNonprofitIdsWhereUserIsAdmin(
  nonprofitIds: UUID[] | string[],
  loggedInUser: PersonalUserResponse | undefined
): UUID[] {
  if (!nonprofitIds?.length || !loggedInUser) {
    return [];
  }

  const nonprofitUUIDs = nonprofitIds.filter(
    (id): id is UUID => UUID.decode(id)._tag === "Right"
  );

  const isSuperAdmin = atLeastAsPrivilegedAs({
    level: loggedInUser.adminLevelForDisplay,
    compareAgainst: AdminLevel.SUPER_ADMIN,
  });

  if (isSuperAdmin) {
    return nonprofitUUIDs.filter((id): id is UUID => UUID.is(id));
  }

  const adminFor = loggedInUser?.adminFor ?? [];
  const confirmedAdminIds = new Set(
    adminFor
      .filter((item) => item.status === NonprofitAdminStatus.CONFIRMED)
      .map((item) => item.nonprofit.id)
  );

  return nonprofitUUIDs.filter(
    (id): id is UUID => UUID.is(id) && confirmedAdminIds.has(id)
  );
}
